import { Link, useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { QuoteRequestRow } from "@shared-core/data/supplier-portal-adapter";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  QUOTE_REQUEST_WORDING,
  quantityLabel,
  statusTone,
  weekdayDayMonth,
} from "@shared-core/domain/supplier-quote-request";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";

/**
 * `/quote-requests` (login only): every quote request addressed to the supplier, latest asked first. Paging is the
 * server's (docs/list-endpoints.md); the page only renders.
 */
export function QuoteRequestsPage() {
  const adapter = useSupplierPortalAdapter();
  const [params] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const list = useLoad(() => adapter.listQuoteRequests(page), `${page}`);
  const columns = QUOTE_REQUEST_WORDING.list.columns;

  const pageHref = (n: number) => (n > 1 ? `?page=${n}` : "?");

  return (
    <div className="grid gap-4">
      <h1 className="sr-only">{QUOTE_REQUEST_WORDING.list.heading}</h1>
      {list.data ? (
        list.data.items.length === 0 ? (
          <Card>
            <EmptyState title={QUOTE_REQUEST_WORDING.list.empty} />
          </Card>
        ) : (
          <>
            <Card className="gap-0 overflow-x-auto py-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{columns.request}</TableHead>
                    <TableHead>{columns.what}</TableHead>
                    <TableHead>{columns.quantities}</TableHead>
                    <TableHead>{columns.replyBy}</TableHead>
                    <TableHead>{columns.status}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.data.items.map((row) => (
                    <RequestRow key={row.supplier_quote_no} row={row} />
                  ))}
                </TableBody>
              </Table>
            </Card>
            {list.data.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                Page {list.data.page} of {list.data.totalPages}
                {page > 1 && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page - 1)}>Previous</Link>
                  </Button>
                )}
                {page < list.data.totalPages && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page + 1)}>Next</Link>
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : list.error ? (
        <LoadFailed
          what="your quote requests"
          message={mutationErrorMessage(
            list.error,
            "Something went wrong reaching Pollards.",
          )}
          onRetry={list.reload}
        />
      ) : (
        <LoadingCard what="your quote requests" />
      )}
    </div>
  );
}

function RequestRow({ row }: { row: QuoteRequestRow }) {
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        <Link
          className="underline-offset-2 hover:underline"
          to={paths.supplierQuoteRequest({
            supplierQuoteNo: row.supplier_quote_no,
          })}
        >
          {row.supplier_quote_no}
        </Link>
      </TableCell>
      <TableCell className="whitespace-normal">
        {row.specification_first_line}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.quantities.map(quantityLabel).join(", ")}
      </TableCell>
      <TableCell>
        {row.reply_by ? weekdayDayMonth(row.reply_by) : "—"}
      </TableCell>
      <TableCell>
        <StatusPill
          label={row.status_label}
          tone={statusTone(row.status_for_supplier)}
        />
      </TableCell>
    </TableRow>
  );
}
